import api from './api';

/**
 * EDI Events (cms.edi_events) — the sent/unsent + delete/undelete action. The grid
 * (read-only /tablemaint engine, key 'edi-events') combines main + archive rows;
 * this only ever writes a MAIN edi_events row (archive rows are locked).
 */
export async function updateEdiEvent(
  recno: number,
  patch: { sentdatetime?: string; deleted?: string },
): Promise<void> {
  await api.put(`/edi-events/${recno}`, patch);
}

/** Sources the EDI Events history "Retrigger" action supports (V1 retriggeredi). */
export type EdiRetriggerSource =
  | 'edievents'
  | 'edisentinvoices'
  | 'ediinventoryadj'
  | 'edieventsarchive';

/**
 * Retrigger an EDI record from the order's EDI Events history so the EDI script
 * re-sends it on its next run — clears the sent marker (edi_events / edi_inventoryadj),
 * flags edi_sentinvoices.resend='Y', or lifts an archived event back into edi_events.
 * Mirrors V1 main.pl `retriggeredi`.
 */
export async function retriggerEdiEvent(source: EdiRetriggerSource, recno: number): Promise<void> {
  await api.post('/edi-events/retrigger', { source, recno });
}

/**
 * Retrigger many records of one source in a single request — the grid's "retrigger
 * everything in view" action. Same write as {@link retriggerEdiEvent}, which is why
 * it shares the source vocabulary: the per-source meaning lives in one place on the
 * server, so a new source never means two things.
 *
 * All-or-nothing: every record gets the identical flag, so the only way one can fail
 * is a database error that would fail all of them. Returns how many rows actually
 * changed, which can be lower than requested when a record was deleted in between.
 */
export async function retriggerEdiEventsBulk(
  source: EdiRetriggerSource,
  recnos: number[],
): Promise<{ requested: number; retriggered: number }> {
  const { data } = await api.post('/edi-events/retrigger-bulk', { source, recnos });
  return { requested: data?.requested ?? recnos.length, retriggered: data?.retriggered ?? 0 };
}
